// Phase 22A — Attendance Time-Window admin config.
//
// Manages `attendance_time_window` documents. UX:
//   - Table of all windows (scope_type · scope target · open · close · active · actions)
//   - Create dialog with scope-type picker (role|zone) + scope-target select
//     + time-of-day pickers (HH:MM) + is_active switch
//   - Note: punch-in is NEVER blocked by these windows — only flagged with
//     `out_of_window: True, window_violation: "before"|"after"`
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function AttendanceWindows() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);  // "new" | row obj
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance-windows", { params: { page_size: 200 } });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [z, r] = await Promise.all([
          api.get("/zones"),
          api.get("/roles"),
        ]);
        setZones(z.data.items || z.data || []);
        setRoles(r.data.items || r.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    load();
  }, [load]);

  const zoneMap = useMemo(() => Object.fromEntries(zones.map(z => [z.id, z])), [zones]);
  const roleMap = useMemo(() => Object.fromEntries(roles.map(r => [r.code, r])), [roles]);
  const scopeLabel = (r) => r.scope_type === "role"
    ? (roleMap[r.scope_id]?.name || r.scope_id)
    : (zoneMap[r.scope_id]?.name || r.scope_id);

  const remove = async (r) => {
    try {
      await api.delete(`/attendance-windows/${r.id}`);
      toast.success("Window removed");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="attendance-windows-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Attendance windows" }]}
        title="Attendance time windows"
        subtitle="Per-zone shift windows for punch-in / punch-out validation."
        favorite={{ key: "admin.attendance-windows", type: "screen", label: "Attendance time windows", route: "/admin/attendance-windows" }}
        testid="attendancewindows-page-header"
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Attendance Time Windows</h1>
          <p className="text-xs text-neutral-500 mt-0.5 max-w-xl">
            Configure punch-in open/close windows per role or zone.
            Punch-ins outside the window are <b>flagged</b> — never blocked.
            Role-scope wins over Zone-scope for the same user.
          </p>
        </div>
        <Button className="tulsi-gradient text-white" onClick={() => setEditing("new")}
                data-testid="attn-window-new">
          <Plus className="h-4 w-4 mr-1" /> New window
        </Button>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <Table data-testid="attn-window-table">
            <TableHeader>
              <TableRow>
                <TableHead>Scope</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Open</TableHead>
                <TableHead>Close</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" />
                </TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-neutral-400 text-sm">
                  No windows configured. All punch-ins accepted without a window flag.
                </TableCell></TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`attn-window-row-${r.id}`}>
                  <TableCell>
                    <Badge variant={r.scope_type === "role" ? "default" : "secondary"}
                           className={r.scope_type === "role" ? "bg-[#6D2C91] text-white" : ""}>
                      {r.scope_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-neutral-900">
                    {scopeLabel(r)}
                    <div className="text-[10px] font-mono text-neutral-400 mt-0.5">{r.scope_id}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    <Clock className="h-3 w-3 inline mr-1 text-neutral-400" />
                    {r.punch_in_open_hhmm}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{r.punch_in_close_hhmm}</TableCell>
                  <TableCell>
                    <Badge className={r.is_active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-500"}>
                      {r.is_active ? "active" : "inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setEditing(r)}
                            data-testid={`attn-window-edit-${r.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setConfirmDel(r)}
                            data-testid={`attn-window-delete-${r.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="text-xs text-neutral-500 flex items-start gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-500" />
        <div>
          Windows are advisory only. Field users are never blocked — the record just gets
          <code className="px-1 py-0.5 rounded bg-neutral-100 mx-1">out_of_window: True</code>
          with <code className="px-1 py-0.5 rounded bg-neutral-100 mx-1">window_violation</code>.
        </div>
      </div>

      {editing && (
        <WindowDialog editing={editing} roles={roles} zones={zones}
                       onClose={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}

      <Dialog open={!!confirmDel} onOpenChange={v => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove window?</DialogTitle>
            <DialogDescription>
              Removing the window means all future punch-ins for {confirmDel ? scopeLabel(confirmDel) : ""} will land without any window flag.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700"
                    onClick={() => remove(confirmDel)}
                    data-testid="attn-window-delete-confirm">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WindowDialog({ editing, roles, zones, onClose, onSaved }) {
  const isNew = editing === "new";
  const [scopeType, setScopeType] = useState(isNew ? "role" : editing.scope_type);
  const [scopeId, setScopeId] = useState(isNew ? "" : editing.scope_id);
  const [openHhmm, setOpenHhmm] = useState(isNew ? "09:00" : editing.punch_in_open_hhmm);
  const [closeHhmm, setCloseHhmm] = useState(isNew ? "11:00" : editing.punch_in_close_hhmm);
  const [isActive, setIsActive] = useState(isNew ? true : editing.is_active);
  const [saving, setSaving] = useState(false);

  const opts = scopeType === "role"
    ? roles.map(r => ({ id: r.code, name: r.name }))
    : zones.map(z => ({ id: z.id, name: z.name }));

  const save = async () => {
    if (!scopeId) { toast.error("Select a scope target"); return; }
    if (openHhmm >= closeHhmm) { toast.error("Open must be before Close"); return; }
    setSaving(true);
    try {
      const body = { scope_type: scopeType, scope_id: scopeId,
                     punch_in_open_hhmm: openHhmm, punch_in_close_hhmm: closeHhmm,
                     is_active: isActive };
      if (isNew) await api.post("/attendance-windows", body);
      else await api.patch(`/attendance-windows/${editing.id}`, body);
      toast.success(isNew ? "Window created" : "Window updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="attn-window-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New" : "Edit"} attendance window</DialogTitle>
          <DialogDescription>
            Defines the punch-in open/close range. Rows outside the range are flagged, never blocked.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Scope type</Label>
            <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeId(""); }}
                    disabled={!isNew}>
              <SelectTrigger data-testid="attn-window-scope-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="zone">Zone</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{scopeType === "role" ? "Role" : "Zone"}</Label>
            <Select value={scopeId} onValueChange={setScopeId} disabled={!isNew}>
              <SelectTrigger data-testid="attn-window-scope-id"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {opts.map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Open (HH:MM)</Label>
            <Input type="time" value={openHhmm} onChange={e => setOpenHhmm(e.target.value)}
                   data-testid="attn-window-open" />
          </div>
          <div>
            <Label>Close (HH:MM)</Label>
            <Input type="time" value={closeHhmm} onChange={e => setCloseHhmm(e.target.value)}
                   data-testid="attn-window-close" />
          </div>
          <div className="col-span-2 flex items-center gap-3">
            <Switch checked={isActive} onCheckedChange={setIsActive}
                    data-testid="attn-window-active" />
            <Label className="text-sm">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="tulsi-gradient text-white"
                  data-testid="attn-window-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
